import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2'; // <-- Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // <-- Import the styles
import './Prompt.css';

const Prompt = () => {
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for editing existing prompts
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  // State for creating a new prompt
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPrompt, setNewPrompt] = useState({
    name: '',
    price: '',
    prompt: '',
  });

  const ADMIN_BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchPrompts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${ADMIN_BACKEND_URL}/api/prompts`);
      setPrompts(response.data);
      setError(null);
    } catch (err) {
      // Set the on-page error, but also show an alert
      setError('Failed to fetch prompts. Please ensure the backend is running.');
      // MODIFIED: Added Swal for immediate feedback on fetch error
      Swal.fire({
        icon: 'error',
        title: 'Failed to Load Prompts',
        text: err.message || 'Could not connect to the server.',
      });
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [ADMIN_BACKEND_URL]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const handleEditClick = (prompt) => {
    setEditingId(prompt._id);
    setEditText(prompt.prompt);
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSaveClick = async (promptId) => {
    try {
      const originalPrompt = prompts.find((p) => p._id === promptId);
      const payload = { ...originalPrompt, prompt: editText };
      await axios.put(`${ADMIN_BACKEND_URL}/api/prompts/${promptId}`, payload);
      
      // MODIFIED: Replaced alert with Swal Toast for success
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      Toast.fire({ icon: 'success', title: 'Prompt updated successfully!' });
      
      setEditingId(null);
      setEditText('');
      fetchPrompts();
    } catch (err) {
      console.error('Failed to save prompt:', err);
      // MODIFIED: Replaced setError with Swal for save error
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || 'An error occurred while saving the prompt.',
      });
    }
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setNewPrompt({ name: '', price: '', prompt: '' });
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleNewPromptChange = (e) => {
    const { name, value } = e.target;
    setNewPrompt((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newPrompt.name || !newPrompt.price || !newPrompt.prompt) {
      // MODIFIED: Replaced setError with Swal for validation
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'All fields (Name, Price, and Prompt) are required.',
      });
      return;
    }

    try {
      const payload = { ...newPrompt, price: Number(newPrompt.price) };
      await axios.post(`${ADMIN_BACKEND_URL}/api/prompts`, payload);
      
      // MODIFIED: Added Swal for success
      Swal.fire({
        icon: 'success',
        title: 'Prompt Created!',
        text: `The new prompt "${payload.name}" has been added.`,
      });

      handleCloseCreateModal();
      fetchPrompts();
    } catch (err) {
      console.error('Failed to create prompt:', err);
      // MODIFIED: Replaced setError with Swal for create error
      Swal.fire({
        icon: 'error',
        title: 'Creation Failed',
        text: err.response?.data?.message || 'An error occurred while creating the prompt.',
      });
    }
  };
  
  // ... The rest of the component JSX remains unchanged ...
  return (
    <div className="prompt-view-container">
      <Helmet>
        <title>Manage Prompts | GN VISA Admin</title>
        <meta name="description" content="View and manage all available system prompts." />
      </Helmet>
      
      <div className="page-header">
        <h1>System Prompts</h1>
        <button className="button-add-new" onClick={handleOpenCreateModal}>
          + Add New Prompt
        </button>
      </div>

      {isLoading && <p>Loading prompts...</p>}
      {error && <p className="error-message">{error}</p>}
      
      {!isLoading && !error && (
        <div className="prompt-list">
          {prompts.length > 0 ? (
            prompts.map((prompt) => (
              <div key={prompt._id} className="prompt-card">
                <div className="prompt-card-header">
                  <h3>{prompt.name || 'No Name'}</h3>
                  <span className="prompt-price">
                    Price: ₹{prompt.price !== undefined ? prompt.price : 'N/A'}
                  </span>
                </div>
                <div className="prompt-card-body">
                  <h4>Prompt Template:</h4>
                  {editingId === prompt._id ? (
                    <textarea
                      className="prompt-edit-textarea"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                  ) : (
                    <pre className="prompt-text-display">
                      {prompt.prompt}
                    </pre>
                  )}
                </div>
                <div className="prompt-card-footer">
                  <small>
                    Created At: {prompt.createdAt ? new Date(prompt.createdAt).toLocaleString() : 'Not available'}
                  </small>
                  <div className="prompt-card-actions">
                    {editingId === prompt._id ? (
                      <>
                        <button className="button-save" onClick={() => handleSaveClick(prompt._id)}>Save</button>
                        <button className="button-cancel" onClick={handleCancelClick}>Cancel</button>
                      </>
                    ) : (
                      <button className="button-edit" onClick={() => handleEditClick(prompt)}>Edit</button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>No prompts found in the database. Click "Add New Prompt" to create one.</p>
          )}
        </div>
      )}

      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <form onSubmit={handleCreateSubmit}>
              <h2>Create New Prompt</h2>
              <div className="form-group">
                <label htmlFor="name">Prompt Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newPrompt.name}
                  onChange={handleNewPromptChange}
                  placeholder="e.g., Smart, Advanced"
                />
              </div>

              <div className="form-group">
                <label htmlFor="price">Price (₹)</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={newPrompt.price}
                  onChange={handleNewPromptChange}
                  placeholder="e.g., 9999"
                />
              </div>

              <div className="form-group">
                <label htmlFor="prompt">Prompt Template</label>
                <textarea
                  id="prompt"
                  name="prompt"
                  value={newPrompt.prompt}
                  onChange={handleNewPromptChange}
                  rows="8"
                  placeholder="Enter the prompt text here..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="button-cancel" onClick={handleCloseCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="button-save">
                  Save Prompt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prompt;